import type { Dispatch, ReactNode, SetStateAction } from "react";

import type { IWizardLegendItemSettings } from "@similarweb/ui-components";

import type { ICampaignsConsumptionMetrics } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";

export interface ICampaignsWizardNavButtonsProps {
    isNextDisabled: boolean;
    isLoading?: boolean;
    onBackStepClick: VoidFunction;
    onNextStepClick: VoidFunction;
    activePageIndex: number;
    numberOfPages: number;
    tooltipContent?: string;
}

export enum ECampaignPagesError {
    /* eslint-disable @typescript-eslint/naming-convention */
    EMPTY_OR_DUPLICATE_SITE_STEP_ERROR = "siteStepError",
    EMPTY_KEYWORDS_STEP_ERROR = "keywordsStepError",
    EXCEED_MAX_KEYWORDS_STEP_ERROR = "exceedMaxKeywordsStepError",
    SEARCH_ENGINES_NOT_ASSIGNED_ERROR = "searchEnginesNotAssignedError",
    NO_DATA = "noData",
    MAX_LENGTH = "maxLength",
    MINIMUM_LENGTH = "minimumLength",
    DUPLICATE_NAME = "duplicateName",
    EMPTY_FIELD = "emptyField",
    INVALID_TEXT = "invalidText",
    SITE_MAX_LENGTH = "siteMaxLength",
    INVALID_CAMPAIGN_NAME = "invalidName",
    INVALID_CAMPAIGN_NAME_BOUNDARIES = "invalidNameBoundaries",
    /* eslint-enable @typescript-eslint/naming-convention */
}

type CampaignsPagesError = ECampaignPagesError | null;

interface ICampaignField {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any;
    hasError: boolean;
}

export interface ICampaignsFieldsCollection {
    [key: string]: ICampaignField;
}

export interface ICampaignsWizardStepProps {
    fields: ICampaignsFieldsCollection;
    legendData: IWizardLegendItemSettings;
    pageError?: CampaignsPagesError;
    shouldShowKeywordCounter: boolean;
    footerWidth: number;
}

export interface ICampaignsWizardState {
    pages: ICampaignsWizardStepProps[];
    wizardHasError?: boolean;
    campaignsFields: Record<string, string>;
}

export interface ICampaignsContext {
    children: ReactNode;
    defaultState: ICampaignsWizardState;
    amountOfKeywordsRemaining: number;
}

export const SORT_ORDER = [
    "Country",
    "Province",
    "State",
    "Territory",
    "Union Territory",
    "Canton",
    "Okrug",
    "Governorate",
    "Prefecture",
    "Region",
    "Autonomous Community",
    "County",
    "District",
    "City",
    "City Region",
    "Municipality",
    "Borough",
    "Neighborhood",
    "Postal Code",
] as const;

export const NON_VALID_LOCATION_TYPES = [
    "National Park",
    "TV Region",
    "Congressional District",
    "University",
    "Airport",
    "Department",
    "Quarter",
    "Sub-Ward",
    "district",
    "Commune",
    "Division",
    "Colloquial Area",
    "Sub-District",
    "Municipality District",
    "Barrio",
    "neighborhood",
] as const;

export interface ISupportedLocationByGoogle {
    countryCode?: string;
    canonicalName?: string;
    type?: (typeof SORT_ORDER)[number];
    name: string;
}
export interface ISupportedLanguageByGoogle {
    locale: {
        [local: string]: {
            description: string;
            domain: string;
        };
    };
}
export interface ISupportedLanguagesAndLocale {
    locale: string;
    description: string;
}

export interface ILocationDetailsToPresent {
    id: string | number;
    text: string;
    type: (typeof SORT_ORDER)[number];
    selected?: boolean;
    active?: boolean;
    permitted?: boolean;
    height?: number;
    isChild?: boolean;
    wrapper?: unknown;
}
export interface ICampaignLocationAndLanguage {
    locationsList: ILocationDetailsToPresent[];
    languagesList: ISupportedLanguagesAndLocale[];
}

export interface ICampaignsPageValidator {
    isPageHasErrors?: () => boolean;
    beforeNext?: () => Promise<boolean>;
}
export interface ICampaignsWizardContext {
    wizardState: ICampaignsWizardState;
    setWizardState: Dispatch<SetStateAction<ICampaignsWizardState>>;

    activePageIndex: number;
    amountOfKeywordsRemaining: number;
    setActivePageIndex: Dispatch<SetStateAction<number>>;
    defaultState: ICampaignsWizardState;
    campaignsConsumptionMetrics: ICampaignsConsumptionMetrics;
}

export const forbiddenLanguages = ["United States Virgin Islands", "Latin American Spanish"];

/* list of languages which we dont want to be the 1st and pre-selected in the languages dropdown in the
   RT campaign wizard (when we select a specific location). For example, for the Spain location list
   of supported languages should be: Spanish (default, pre-selected), Catalan, English */
export const unwantedLanguages = ["Catalan"];

/* stands for all the countries that contains the forbidden location */
export const forbiddenLocations = [
    "United States Minor Outlying Islands",
    "Antarctica",
    "French Southern and Antarctic Lands",
    "South Georgia and the South Sandwich Islands",
    "Belarus*",
];
